/**
 * Utility functions for formatting employee names in the client portal.
 * In the client portal, we show first name with last name initial to protect employee privacy.
 */

/**
 * Formats an employee name to show first name with last name initial in the client portal.
 * Example: "John Smith" becomes "John S"
 * 
 * @param {Object|string} employee - Employee object with name/firstName/lastName, or a name string
 * @returns {string} - Formatted name (first name + last name initial)
 */
export const formatEmployeeNameForClient = (employee) => {
  // Handle string input (already formatted name)
  if (typeof employee === 'string') {
    // Extract first name and last name initial from string
    const nameParts = employee.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      const firstName = nameParts[0];
      const lastNameInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
      return `${firstName} ${lastNameInitial}`;
    }
    return nameParts[0] || employee;
  }

  // Handle object input
  if (!employee) {
    return 'Staff Member';
  }

  // If it's "Any Available", return as is
  if (employee.isAnyAvailable || employee.name === 'Any Available') {
    return 'Any Available';
  }

  // If we have both firstName and lastName, format as "FirstName L"
  if (employee.firstName && employee.lastName) {
    const firstName = employee.firstName.trim();
    const lastNameInitial = employee.lastName.trim().charAt(0).toUpperCase();
    return `${firstName} ${lastNameInitial}`;
  }

  // If we only have firstName, return just the first name
  if (employee.firstName) {
    return employee.firstName.trim();
  }

  // If we have a name string, try to extract first name and last name initial
  if (employee.name) {
    const nameParts = employee.name.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      const firstName = nameParts[0];
      const lastNameInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
      return `${firstName} ${lastNameInitial}`;
    }
    return nameParts[0] || employee.name;
  }

  // Fallback
  return 'Staff Member';
};

/**
 * Formats employee initials for avatars (first name initial only).
 * 
 * @param {Object|string} employee - Employee object or name string
 * @returns {string} - First letter of first name
 */
export const getEmployeeInitial = (employee) => {
  const firstName = formatEmployeeNameForClient(employee);
  if (firstName === 'Any Available') {
    return 'A';
  }
  return firstName.charAt(0).toUpperCase() || 'S';
};

/**
 * Formats employee initials for avatars (first name + last name initial).
 * This is used for avatar display where we still want two letters.
 * 
 * @param {Object} employee - Employee object with firstName/lastName
 * @returns {string} - Two-letter initials (first name + last name initial)
 */
export const getEmployeeInitials = (employee) => {
  if (!employee) return 'SM';
  
  if (employee.isAnyAvailable || employee.name === 'Any Available') {
    return 'AA';
  }

  if (employee.firstName && employee.lastName) {
    return `${employee.firstName[0]}${employee.lastName[0]}`.toUpperCase();
  }
  
  if (employee.firstName) {
    return `${employee.firstName[0]}${employee.firstName[1] || employee.firstName[0]}`.toUpperCase();
  }
  
  if (employee.name) {
    const nameParts = employee.name.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return employee.name.substring(0, 2).toUpperCase() || 'SM';
  }
  
  return 'SM';
};
